import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { vatsColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { VatService } from '../../services/entity-services/vat.services'
import { SchemaUtil } from '../../utils'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-vats',
  templateUrl: 'vats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VatsComponent extends BaseListing implements OnInit {
  private vatService = inject(VatService)

  listingColumns = signal(vatsColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  // todo remove if safe:  filters = signal(structuredClone(DEFAULT_FILTERS))
  filters = signal(
    this.loadViewSettings('vats')?.filters ? this.loadViewSettings('vats').filters : structuredClone(DEFAULT_FILTERS)
  )

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_VAT_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const v = this.filters()
    const f: Record<string, any> = {}
    if (v.name?.length) f['name'] = v.name
    return f
  })

  readonly queryVats = injectInfiniteQuery(() => ({
    queryKey: ['vats', this.sortProp(), this.sortDir()],
    queryFn: async ({ pageParam }) => {
      return await this.vatService
        .getVats(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          SchemaUtil.vat
        )
        .then(result => {
          this.saveViewSettings('vats', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryVats.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryVats.data()
    })
  }

  ngOnInit() {
    const view = this.loadViewSettings('vats')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('vats')
  }

  setFilterName(value: string) {
    this.filters.update(f => ({ ...f, name: value }))
  }

  clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryVats.refetch()
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
